import React from 'react';
import PropTypes from 'prop-types';
import './SmsConsentCheckbox.css';

export const SMS_CONSENT_DOC_TYPE = 'sms_marketing_consent';
export const SMS_CONSENT_VERSION = '2026-07-02-minimal';
export const SMS_CONSENT_OPTIONAL_ERROR =
  'SMS marketing consent is optional and cannot be required to continue.';
export const SMS_CONSENT_PRIMARY_TEXT =
  'I agree to recurring automated marketing texts from Lumiwealth Inc.';
export const SMS_CONSENT_DETAILS_TEXT =
  'Consent not required to buy. Msg/data rates may apply. Frequency varies. Reply STOP to opt out.';
export const SMS_CONSENT_DISCLOSURE =
  `${SMS_CONSENT_PRIMARY_TEXT} ${SMS_CONSENT_DETAILS_TEXT}`;

export const shouldShowSmsConsent = (phoneNumber) => Boolean(String(phoneNumber || '').trim());

const currentPagePath = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search || ''}`;
};

export const buildSmsConsentPayload = ({
  source,
  phoneNumber,
  accepted,
  pagePath,
}) => {
  const normalizedPhone = String(phoneNumber || '').trim();
  if (!normalizedPhone) return null;

  const now = new Date().toISOString();
  const acceptedValue = Boolean(accepted);

  return {
    docType: SMS_CONSENT_DOC_TYPE,
    version: SMS_CONSENT_VERSION,
    accepted: acceptedValue,
    status: acceptedValue ? 'accepted' : 'declined',
    respondedAt: now,
    ...(acceptedValue ? { acceptedAt: now } : { declinedAt: now }),
    channel: 'sms',
    consentType: 'marketing_sms',
    consentNotConditionOfPurchase: true,
    source: source || 'botspot_phone_form',
    pagePath: pagePath || currentPagePath(),
    phoneNumber: normalizedPhone,
    disclosureText: SMS_CONSENT_DISCLOSURE,
    optOutInstruction: 'Reply STOP to opt out',
  };
};

export default function SmsConsentCheckbox({
  id,
  checked,
  onChange,
  disabled = false,
  error = '',
  className = '',
  tone = 'dark',
  compact = false,
}) {
  const inputId = id || 'sms-consent-checkbox';

  return (
    <div
      className={[
        'sms-consent-checkbox',
        `sms-consent-checkbox--${tone}`,
        compact ? 'sms-consent-checkbox--compact' : '',
        error ? 'sms-consent-checkbox--error' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid="sms-consent-checkbox"
    >
      <label className="sms-consent-checkbox__label">
        <input
          id={inputId}
          type="checkbox"
          checked={Boolean(checked)}
          onChange={(event) => onChange?.(event.target.checked)}
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
        <span className="sms-consent-checkbox__text">
          <span className="sms-consent-checkbox__primary">
            {SMS_CONSENT_PRIMARY_TEXT}
          </span>
          <span className="sms-consent-checkbox__details">
            {SMS_CONSENT_DETAILS_TEXT}
          </span>
        </span>
      </label>
      {error ? (
        <div id={`${inputId}-error`} className="sms-consent-checkbox__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

SmsConsentCheckbox.propTypes = {
  id: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  tone: PropTypes.oneOf(['dark', 'light']),
  compact: PropTypes.bool,
};
